import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from './Toast';
import { timeAgo } from '../lib/constants';

const DEV_ROOM_ID = '00000000-0000-0000-0000-000000000002';
const LS_KEY = 'shoptrack_dev_todos'; // shared with the Shop Tasks panel in dev mode

function devLoad() {
  try { const s = localStorage.getItem(LS_KEY); return s ? JSON.parse(s) : []; }
  catch { return []; }
}
function devSave(all) { try { localStorage.setItem(LS_KEY, JSON.stringify(all)); } catch {} }

// Tasks for one claim — shop-wide (everyone in the room sees them), but only
// the creator can delete their own. Shares the `todos` table with Shop Tasks,
// so a task added here also shows in the creator's Shop Tasks list.
export default function TasksTab({ claim, session, room, tl }) {
  const isDev = room.id === DEV_ROOM_ID;
  const myId = session.user.id;
  const claimLabel = `${claim.car || ''} · ${claim.plate || ''}`.replace(/^ · | · $/g, '').trim() || null;

  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const load = useCallback(async () => {
    if (isDev) {
      setTodos(devLoad().filter(t => t.claim_id === claim.id));
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('todos')
      .select('*')
      .eq('room_id', room.id)
      .eq('claim_id', claim.id)
      .order('done', { ascending: true })
      .order('created_at', { ascending: false });
    if (data) setTodos(data);
    setLoading(false);
  }, [room.id, claim.id, isDev]);

  useEffect(() => {
    load();
    if (isDev) return;
    const ch = supabase.channel(`claim-todos-${claim.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'todos', filter: `claim_id=eq.${claim.id}` }, load)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [claim.id, load, isDev]);

  async function addTask(e) {
    e.preventDefault();
    if (!text.trim()) return;
    setAdding(true);
    if (isDev) {
      const t = { id: `td-${Date.now()}`, room_id: room.id, user_id: myId, claim_id: claim.id, claim_label: claimLabel, text: text.trim(), done: false, done_at: null, created_at: new Date().toISOString() };
      const all = devLoad(); devSave([t, ...all]);
      setTodos(prev => [t, ...prev]);
      setText(''); setAdding(false);
      return;
    }
    const row = { room_id: room.id, user_id: myId, claim_id: claim.id, claim_label: claimLabel, text: text.trim(), done: false };
    const temp = { ...row, id: `temp-${Date.now()}`, done_at: null, created_at: new Date().toISOString() };
    const prevText = text;
    setTodos(prev => [temp, ...prev]);
    setText(''); setAdding(false);
    const { data, error } = await supabase.from('todos').insert(row).select().single();
    if (error) {
      setTodos(prev => prev.filter(t => t.id !== temp.id));
      setText(prevText);
      toast('Could not add the task — check your connection and try again.', 'error');
      return;
    }
    setTodos(prev => prev.map(t => (t.id === temp.id ? data : t)));
  }

  async function toggleDone(todo) {
    const nowDone = !todo.done;
    const patch = { done: nowDone, done_at: nowDone ? new Date().toISOString() : null };
    if (isDev) {
      const all = devLoad().map(t => t.id === todo.id ? { ...t, ...patch } : t);
      devSave(all);
      setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, ...patch } : t));
      return;
    }
    setTodos(prev => prev.map(t => (t.id === todo.id ? { ...t, ...patch } : t)));
    const { error } = await supabase.from('todos').update(patch).eq('id', todo.id);
    if (error) {
      setTodos(prev => prev.map(t => (t.id === todo.id ? { ...t, done: todo.done, done_at: todo.done_at } : t)));
      toast('Could not update the task — check your connection and try again.', 'error');
    }
  }

  async function deleteTask(id) {
    if (!window.confirm('Delete this task?')) return;
    setRemovingId(id);
    await new Promise(r => setTimeout(r, 180));
    setRemovingId(null);
    if (isDev) {
      const all = devLoad().filter(t => t.id !== id); devSave(all);
      setTodos(prev => prev.filter(t => t.id !== id));
      return;
    }
    const removed = todos.find(t => t.id === id);
    setTodos(prev => prev.filter(t => t.id !== id));
    const { error } = await supabase.from('todos').delete().eq('id', id);
    if (error && removed) {
      setTodos(prev => [removed, ...prev]);
      toast('Could not delete the task — check your connection and try again.', 'error');
    }
  }

  const open = todos.filter(t => !t.done);
  const done = todos.filter(t => t.done);
  const fieldBg = tl ? 'var(--sp-bg2)' : 'var(--bg3)';
  const fieldBorder = tl ? 'var(--sp-border)' : 'var(--border)';

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Add task */}
      <form onSubmit={addTask} style={{ position: 'relative' }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addTask(e); } }}
          placeholder="Add a task for this claim..."
          rows={3}
          style={{ width: '100%', resize: 'none', fontSize: 15, lineHeight: 1.6, padding: '12px 52px 12px 14px', background: fieldBg, border: `1px solid ${fieldBorder}`, borderRadius: 8, color: 'var(--text)', outline: 'none' }}
        />
        <button type="submit" disabled={adding || !text.trim()} className="raise-3d-orange" style={{ position: 'absolute', bottom: 12, right: 10, background: 'var(--orange)', color: '#16100b', border: 'none', borderRadius: 8, width: 36, height: 36, cursor: text.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: text.trim() ? 1 : 0.35, transition: 'opacity 0.15s' }}>
          {adding ? <div className="spinner" style={{ width: 14, height: 14 }} /> : <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>}
        </button>
      </form>

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><div className="spinner" /></div>}
      {!loading && todos.length === 0 && (
        <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text3)' }}>
          <div style={{ fontSize: 26, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text2)' }}>No tasks for this claim yet</div>
          <div style={{ fontSize: 12.5, marginTop: 4 }}>Add one above — the whole shop can see it.</div>
        </div>
      )}

      {open.map(todo => <Row key={todo.id} todo={todo} mine={todo.user_id === myId} removing={removingId === todo.id} onToggle={toggleDone} onDelete={deleteTask} />)}

      {done.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1px', color: 'var(--text3)', textTransform: 'uppercase' }}>Done ({done.length})</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>
          {done.map(todo => <Row key={todo.id} todo={todo} mine={todo.user_id === myId} removing={removingId === todo.id} onToggle={toggleDone} onDelete={deleteTask} />)}
        </>
      )}
    </div>
  );
}

function Row({ todo, mine, removing, onToggle, onDelete }) {
  return (
    <div className={removing ? 'removing' : undefined} style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      background: todo.done ? 'transparent' : 'var(--bg3)',
      border: `1px solid ${todo.done ? 'transparent' : 'var(--border)'}`,
      borderRadius: 10, padding: todo.done ? '6px 4px' : '10px 12px',
      opacity: todo.done ? 0.5 : 1, transition: 'all 0.15s',
    }}>
      <button onClick={() => onToggle(todo)} style={{
        width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 1,
        border: `2px solid ${todo.done ? 'var(--green)' : 'var(--border)'}`,
        background: todo.done ? 'var(--green)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', color: '#fff', fontSize: 12, fontWeight: 800,
      }}>{todo.done ? '✓' : ''}</button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.4, textDecoration: todo.done ? 'line-through' : 'none', color: todo.done ? 'var(--text3)' : 'var(--text)' }}>{todo.text}</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5 }}>{timeAgo(todo.created_at)}</div>
      </div>
      {mine && (
        <button onClick={() => onDelete(todo.id)} title="Delete" style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16, padding: '0 2px', flexShrink: 0, opacity: 0.6 }}>×</button>
      )}
    </div>
  );
}
